import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Container, Row, Col, Card, Modal } from 'react-bootstrap';
import { useAuth } from '../AuthContext';
import { useTheme } from '../../ThemeContext';
import BottomNav from '../BottomNav';
import Codee from '../ui/Codee';
import EmailDeliveryHintAlert from '../ui/EmailDeliveryHintAlert';
import { useNavigate } from 'react-router-dom';
import { PUBLIC_BASE_URL } from '../../config/runtime';

const SubscriptionForm = () => {
  const { userInfo, isLoading, isAuthenticated, payWithBonus, payWithRegular } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [balance, setBalance] = useState(0);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentLink, setPaymentLink] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoFeedback, setPromoFeedback] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [showBonusSuccessAlert, setShowBonusSuccessAlert] = useState(false);
  const [bonusSuccessLines, setBonusSuccessLines] = useState([]);
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const subscriptionPlans = [
    { days: 7, cost: 100 },
    { days: 30, cost: 300 },
    { days: 90, cost: 750 },
    { days: 180, cost: 1350 },
  ];
  const thirtyDayPlan = subscriptionPlans.find((plan) => plan.days === 30);
  const PRICE_PER_DAY_BONUS = 5;
  const bonusDayOptions = [1, 5, 10, 15, 30];

  useEffect(() => {
    const initializeAuth = async () => {
      if (isAuthenticated && !isLoading) {
        setBalance(userInfo.balance);
        if (userInfo.email) {
          setEmail(userInfo.email);
        }
      }
    };
    void initializeAuth();
  }, [userInfo, isLoading, isAuthenticated]);

  const getConfirmedAccountEmail = () => {
    const accountEmail = (userInfo?.email || '').trim();
    return emailPattern.test(accountEmail) ? accountEmail : '';
  };

  const getCardPaymentNote = () => (
    getConfirmedAccountEmail()
      ? 'Выберите тариф, платежная ссылка сформируется сразу.'
      : 'Для оплаты картой сначала подтвердите email в профиле.'
  );

  const handlePlanSelect = (plan) => {
    const confirmedEmail = getConfirmedAccountEmail();
    const nextPromoCode = plan.days === 30 ? promoCode : '';

    setSelectedPlan(plan);
    setPromoFeedback(null);
    setPaymentAmount(null);
    setPaymentLink('');
    setError('');
    if (plan.days !== 30) {
      setPromoCode('');
    }

    if (confirmedEmail) {
      setEmail(confirmedEmail);
      void handleRegularPayment(plan, confirmedEmail, nextPromoCode);
      return;
    }

    setShowModal(true);
  };

  const handleBonusPayment = async (days) => {
    try {
      setLoading(true);
      setError('');

      const data = await payWithBonus(days);

      if (data?.status) {
        if (typeof data.balance === 'number') setBalance(data.balance);
        const untilLine = data?.until
          ? `Подписка продлена до ${data.until} (МСК).`
          : 'Подписка успешно продлена.';
        setBonusSuccessLines([
          untilLine,
          'Доступ к функциям уже обновлен.',
        ]);
        setShowBonusSuccessAlert(true);
      } else {
        const msg = data?.detail || 'Не хватает денег на балансе';
        const b = typeof data?.balance === 'number' ? ` (баланс: ${data.balance}₽)` : '';
        setError(`${msg}${b}`);
      }
    } catch (err) {
      setError(err.message || 'Ошибка при оплате бонусами');
    } finally {
      setLoading(false);
    }
  };

  const handleRegularPayment = async (
    planArg = selectedPlan,
    emailArg = email,
    promoArg = promoCode
  ) => {
    try {
      setError('');
      setPromoFeedback(null);
      setLoading(true);

      const paymentEmail = String(emailArg || '').trim();
      const plan = planArg;
      const normalizedPromo = plan?.days === 30
        ? String(promoArg || '').trim().toUpperCase()
        : '';

      if (!emailPattern.test(paymentEmail)) {
        throw new Error('Неверный формат email');
      }
      if (!plan) {
        throw new Error('План не выбран');
      }

      const data = await payWithRegular(
        paymentEmail,
        plan.days,
        plan.cost,
        normalizedPromo
      );

      if (!data?.paymentLink) {
        throw new Error('Не удалось получить платежную ссылку');
      }

      if (data?.promo) {
        setPromoFeedback(data.promo);
      }

      setEmail(paymentEmail);
      setSelectedPlan(plan);
      if (normalizedPromo) {
        setPromoCode(normalizedPromo);
      }
      setPaymentLink(data.paymentLink);
      if (typeof data?.amount === 'number') {
        setPaymentAmount(data.amount);
      } else {
        setPaymentAmount(plan.cost);
      }
      setShowModal(false);
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Произошла ошибка при оплате');
      setShowModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePromoQuickApply = async () => {
    try {
      setError('');
      setPromoFeedback(null);
      setLoading(true);

      const normalizedPromo = (promoCode || '').trim().toUpperCase();
      if (!normalizedPromo) {
        throw new Error('Введите промокод');
      }
      const accountEmail = getConfirmedAccountEmail();
      if (!accountEmail) {
        throw new Error('Для применения промокода сначала подтвердите email в настройках профиля');
      }
      if (!thirtyDayPlan) {
        throw new Error('Тариф 30 дней недоступен');
      }

      const data = await payWithRegular(
        accountEmail,
        thirtyDayPlan.days,
        thirtyDayPlan.cost,
        normalizedPromo
      );

      if (!data?.promo?.applied) {
        setPaymentLink('');
        setPaymentAmount(null);
        setSelectedPlan('');
        setPromoFeedback(data?.promo || {
          applied: false,
          message: 'Промокод не применился. Проверьте условия и попробуйте еще раз.',
        });
        return;
      }

      if (!data?.paymentLink) {
        throw new Error('Не удалось получить платежную ссылку');
      }

      setPromoCode(normalizedPromo);
      setPromoFeedback(data.promo);
      setSelectedPlan({
        days: Number(data?.days) || thirtyDayPlan.days,
        cost: thirtyDayPlan.cost,
      });
      setPaymentLink(data.paymentLink);
      setPaymentAmount(
        typeof data?.amount === 'number' ? data.amount : thirtyDayPlan.cost
      );
    } catch (err) {
      setPaymentLink('');
      setPaymentAmount(null);
      setSelectedPlan('');
      setError(err.message || 'Ошибка при применении промокода');
    } finally {
      setLoading(false);
    }
  };

  const isDark = theme === 'dark';
  const themeClass = isDark ? 'text-white' : 'text-dark';
  const shellClass = `pb-5 scrollable pay-page-shell pay-safe-top ${isDark ? 'pay-page-shell--dark' : 'pay-page-shell--light'} ${themeClass}`;

  const resetPaymentState = () => {
    setShowModal(false);
    setSelectedPlan('');
    setPaymentLink('');
    setPaymentAmount(null);
    setPromoFeedback(null);
    setPromoCode('');
  };

  const handleCloseBonusSuccessAlert = () => {
    setShowBonusSuccessAlert(false);
    navigate('/');
  };

  const renderCardPlans = () => (
    <div className="pay-option-grid">
      {subscriptionPlans.map((plan) => (
        <Button
          key={plan.days}
          className="pay-option-btn pay-option-btn--card"
          onClick={() => handlePlanSelect(plan)}
          disabled={loading}
          variant="light"
        >
          {plan.days} дней • {plan.cost} ₽
        </Button>
      ))}
    </div>
  );

  const renderPromoQuickBlock = () => {
    const hasConfirmedEmail = Boolean(getConfirmedAccountEmail());

    return (
      <div className="pay-promo-block text-start">
        <div className="pay-promo-head">
          <p className="pay-promo-title">Промокод</p>
          <p className="pay-promo-note">Только для тарифа 30 дней</p>
        </div>

        <div className="d-flex flex-column flex-md-row gap-2">
          <Form.Control
            type="text"
            placeholder="Введите промокод"
            className="pay-promo-input"
            value={promoCode}
            onChange={(e) => setPromoCode((e.target.value || '').toUpperCase())}
            disabled={loading}
            maxLength={64}
          />
          <Button
            variant="light"
            className="pay-promo-apply-btn"
            onClick={handlePromoQuickApply}
            disabled={loading || !promoCode?.trim() || !hasConfirmedEmail}
          >
            {loading ? 'Проверка...' : 'Применить'}
          </Button>
        </div>

        {!hasConfirmedEmail && (
          <Alert variant="warning" className="mt-2 mb-0 text-start pay-glass-alert pay-glass-alert--warning">
            Для промокода сначала привяжите и подтвердите email в настройках профиля.
          </Alert>
        )}

        {promoFeedback && !paymentLink && (
          <Alert
            variant={promoFeedback.applied ? 'success' : 'warning'}
            className={`mt-2 mb-0 text-start pay-glass-alert ${promoFeedback.applied ? 'pay-glass-alert--success' : 'pay-glass-alert--warning'}`}
          >
            {promoFeedback.message}
          </Alert>
        )}
      </div>
    );
  };

  return (
    <div className={`min-vh-100 d-flex flex-column ${isDark ? 'bg-dark' : 'bg-light'}`}>
      <div className={`pay-status-glass ${isDark ? 'pay-status-glass--dark' : 'pay-status-glass--light'}`} aria-hidden="true" />
      <div className={shellClass}>
        <Container>
          <Row className="justify-content-md-center py-2">
            <Col lg={10} xl={8}>
              <Card className="pay-main-card">
                <Card.Body className="pay-main-card-body text-center">
                  {paymentLink && selectedPlan ? (
                    <>
                      <h4 className="pay-main-title">Оплата подписки на {selectedPlan.days} дней</h4>
                      <p className="pay-main-subtitle">Ссылка уже готова. Нажмите кнопку ниже для оплаты картой.</p>
                      {promoFeedback && (
                        <Alert
                          variant={promoFeedback.applied ? 'success' : 'warning'}
                          className={`mt-2 mb-0 text-start pay-glass-alert ${promoFeedback.applied ? 'pay-glass-alert--success' : 'pay-glass-alert--warning'}`}
                        >
                          {promoFeedback.message}
                        </Alert>
                      )}
                      <Alert variant="success" className="mt-3 p-3 pay-policy-alert text-start">
                        Нажимая на кнопку <strong>«Оплатить {paymentAmount ?? selectedPlan.cost} рублей»</strong>,
                        вы соглашаетесь с условиями{' '}
                        <a href={`${PUBLIC_BASE_URL}/agreement.pdf`} target="_blank" rel="noopener noreferrer" className="fw-semibold">
                          Пользовательского соглашения
                        </a>{' '}
                        и{' '}
                        <a href={`${PUBLIC_BASE_URL}/politika-obrabotki-personalnyh-dannyh.pdf`} target="_blank" rel="noopener noreferrer" className="fw-semibold">
                          Политики обработки персональных данных
                        </a>.
                      </Alert>
                      <div className="d-grid mt-3">
                        <Button
                          variant="light"
                          href={paymentLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pay-primary-action"
                        >
                          Оплатить {paymentAmount ?? selectedPlan.cost} ₽
                        </Button>
                      </div>
                    </>
                  ) : balance >= PRICE_PER_DAY_BONUS ? (
                    <>
                      <h4 className="pay-main-title">Продление подписки</h4>
                      <p className="pay-main-subtitle">Выберите удобный способ оплаты: бонусами или картой.</p>
                      <div className="mb-2">
                        <span className="pay-id-badge">
                          ID: <Codee>{userInfo ? userInfo.user_id : null}</Codee>
                        </span>
                      </div>

                      {renderPromoQuickBlock()}

                      <div className="pay-method-card text-start">
                        <div className="pay-method-head">
                          <div className="pay-method-title">Оплата бонусами</div>
                          <span className="pay-method-badge pay-method-badge--bonus">
                            Баланс: {balance.toFixed(2)} ₽
                          </span>
                        </div>
                        <p className="pay-method-note">1 день = {PRICE_PER_DAY_BONUS} ₽ с бонусного баланса</p>
                        <div className="pay-option-grid">
                          {bonusDayOptions.map((d) => {
                            const cost = d * PRICE_PER_DAY_BONUS;
                            const can = balance >= cost;
                            return (
                              <Button
                                key={d}
                                variant="light"
                                className={`pay-option-btn ${can ? 'pay-option-btn--bonus-active' : 'pay-option-btn--bonus-disabled'}`}
                                disabled={loading || !can}
                                onClick={() => handleBonusPayment(d)}
                              >
                                {d} дн • {cost} ₽
                              </Button>
                            );
                          })}
                        </div>
                      </div>

                      <hr className="pay-divider" />

                      <div className="pay-method-card text-start">
                        <div className="pay-method-head">
                          <div className="pay-method-title">Оплата картой</div>
                          <span className="pay-method-badge pay-method-badge--card">Онлайн-оплата</span>
                        </div>
                        <p className="pay-method-note">{getCardPaymentNote()}</p>
                        {renderCardPlans()}
                      </div>
                    </>
                  ) : (
                    <>
                      <h4 className="pay-main-title">Оплата подписки</h4>
                      <p className="pay-main-subtitle">Оплата доступна картой. Бонусов пока недостаточно.</p>
                      <div className="mb-2">
                        <span className="pay-id-badge">
                          ID: <Codee>{userInfo ? userInfo.user_id : null}</Codee>
                        </span>
                      </div>

                      {renderPromoQuickBlock()}

                      <div className="pay-method-card text-start">
                        <div className="pay-method-head">
                          <div className="pay-method-title">Оплата картой</div>
                          <span className="pay-method-badge pay-method-badge--card">Онлайн-оплата</span>
                        </div>
                        <p className="pay-method-note">{getCardPaymentNote()}</p>
                        {renderCardPlans()}
                      </div>
                    </>
                  )}

                  {error && <Alert variant="danger" className="mt-3 mb-0 text-start pay-glass-alert pay-glass-alert--danger">{error}</Alert>}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal show={showModal} onHide={resetPaymentState} centered className={`pay-modal ${isDark ? 'modal-dark text-white' : 'modal-light text-dark'}`}>
        <Modal.Header closeButton>
          <Modal.Title>Подтвердите email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="pay-email-required">
            <p className="mb-2">
              Для оплаты картой нужно подтвердить email в профиле.
            </p>
            <p className="mb-0">
              После подтверждения вернитесь на оплату и выберите тариф еще раз.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" className="pay-modal-btn pay-modal-btn--ghost" onClick={resetPaymentState}>
            Отмена
          </Button>
          <Button
            variant="light"
            className="pay-modal-btn pay-modal-btn--primary"
            onClick={() => {
              resetPaymentState();
              navigate('/profile');
            }}
          >
            Открыть профиль
          </Button>
        </Modal.Footer>
      </Modal>

      <EmailDeliveryHintAlert
        show={showBonusSuccessAlert}
        isDark={isDark}
        onClose={handleCloseBonusSuccessAlert}
        title="Подписка продлена"
        lines={bonusSuccessLines}
        buttonText="Принял"
        ariaLabel="Уведомление об успешной оплате подписки"
      />

      <BottomNav />
    </div>
  );
};

export default SubscriptionForm;
